/**
 * library: recents + bookmarks for inspected messages. Privacy-conscious by
 * design: RECENTS live in sessionStorage (gone when the tab closes), BOOKMARKS
 * live in localStorage (kept until the user removes them, since saving one is a
 * deliberate act). Each entry carries just enough to re-open the message from
 * the home, plus a display line. Everything stays on the device.
 *
 * The bookmark writers return what is ON THE DEVICE, never what would have
 * been there had the write landed. A refused write returns the PREVIOUS list,
 * so the caller that feeds it straight into React state cannot end up showing
 * a filled star over a bookmark that is not stored. That is the whole reason
 * they return a result shape and the recents writers do not: a bookmark is the
 * durable claim ("it stays there until you remove it"), and recents are a
 * within-session convenience that nothing promises will outlive anything.
 *
 * Bookmarks also opt OUT of the debounced storage-toast bridge and are
 * answered at the call site instead. The bridge is budgeted to one toast per
 * kind per 30 seconds, which is right for a preference saved by a slider and
 * wrong here: bookmarking is a deliberate act, and the second message the
 * reader loses matters exactly as much as the first.
 */
import type { EmailAnalysis } from "../email-analysis";
import type { ParsedEmail } from "../email-parser";
import type { StorageErrorKind } from "../utils/storageError";
import { STORAGE_KEYS } from "../utils/storageKeys";
import { readEnvelope, writeEnvelope, type StorageWriteResult } from "./persist";
import { addressLabel, fmtDate } from "./selectors";
import { buildVerdict } from "./verdict";

export interface LibEntry {
  id: string;
  from: string;
  /** The RAW subject, possibly "". The display site localizes the fallback
   * (lib.noSubject), so a stored blank re-localizes on locale switch. */
  subject: string;
  tier: string;
  date: string;
  savedAt: number;
  source: string;
}

const RECENTS_KEY = STORAGE_KEYS.recents;
const BOOKS_KEY = STORAGE_KEYS.bookmarks;
/** Pre-namespace keys, migrated once on read then removed. */
const LEGACY_RECENTS_KEY = "mi.recents";
const LEGACY_BOOKS_KEY = "mi.bookmarks";
const LIB_VERSION = 1;

/** Field validator: kills the raw JSON.parse cast; malformed rows drop. */
function isLibEntry(v: unknown): v is LibEntry {
  if (typeof v !== "object" || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.id === "string" &&
    typeof o.from === "string" &&
    typeof o.subject === "string" &&
    typeof o.tier === "string" &&
    typeof o.date === "string" &&
    typeof o.savedAt === "number" &&
    typeof o.source === "string"
  );
}

/**
 * Accept a stored list if it IS a list, and salvage it row by row.
 *
 * `every(isLibEntry)` looked stricter and was worse: one malformed bookmark,
 * from an interrupted write or a schema change, threw away all sixty of them.
 * A library is a pile of independent rows, so a bad row costs that row.
 */
function isLibList(v: unknown): v is LibEntry[] {
  return Array.isArray(v);
}

/** Drop the rows that do not validate, keep the rest. */
function salvage(list: LibEntry[]): LibEntry[] {
  return list.filter(isLibEntry);
}
const RECENTS_CAP = 8;
const BOOKS_CAP = 60;

/** Small stable non-crypto key (djb2) so the same message dedupes. */
export function libId(source: string): string {
  let h = 5381;
  for (let i = 0; i < source.length; i++) h = ((h << 5) + h + source.charCodeAt(i)) >>> 0;
  return `${h.toString(36)}-${source.length.toString(36)}`;
}

/** Build a library entry from a parsed+analysed message. savedAt stamped here. */
export function entryFrom(
  source: string,
  _parsed: ParsedEmail,
  analysis: EmailAnalysis,
  savedAt: number,
): LibEntry {
  const id = analysis.identity;
  return {
    id: libId(source),
    from: addressLabel(id.from[0]),
    subject: id.subject || "",
    tier: buildVerdict(analysis).tier,
    date: fmtDate(id.date),
    savedAt,
    source,
  };
}

function read(storage: Storage | undefined, key: string, legacyKey: string): LibEntry[] {
  return salvage(
    readEnvelope(storage, key, {
      version: LIB_VERSION,
      validate: isLibList,
      // Bare legacy arrays (old writes and mi.* keys): keep the valid rows.
      migrateLegacy: (bare) => (Array.isArray(bare) ? bare.filter(isLibEntry) : null),
      legacyKeys: [legacyKey],
    }) ?? [],
  );
}
function write(
  storage: Storage | undefined,
  key: string,
  list: LibEntry[],
  opts?: { report?: boolean },
): StorageWriteResult {
  return writeEnvelope(storage, key, LIB_VERSION, list, opts);
}

// Resolved per call (not captured at module load) so test stubs bind and a
// storage that appears late (SSR hydration) is still picked up.
function session(): Storage | undefined {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : undefined;
  } catch {
    return undefined;
  }
}
function local(): Storage | undefined {
  try {
    return typeof localStorage !== "undefined" ? localStorage : undefined;
  } catch {
    return undefined;
  }
}

export function getRecents(): LibEntry[] {
  return read(session(), RECENTS_KEY, LEGACY_RECENTS_KEY);
}
export function addRecent(e: LibEntry): LibEntry[] {
  const list = [e, ...getRecents().filter((x) => x.id !== e.id)].slice(0, RECENTS_CAP);
  write(session(), RECENTS_KEY, list);
  return list;
}
export function clearRecents(): LibEntry[] {
  write(session(), RECENTS_KEY, []);
  return [];
}

/** What a bookmark write actually did, as opposed to what it meant to do. */
export interface BookmarkWrite {
  /** The bookmarks as they now stand on the device. Unchanged if `ok` is false. */
  list: LibEntry[];
  /** True only when the new list reached localStorage. */
  ok: boolean;
  /**
   * Why not, when the storage was present and refused. Absent when the write
   * landed, and absent when this browser stores nothing for this site at all,
   * which is a different sentence and not a failure of this write.
   */
  kind?: StorageErrorKind;
  /** True when the act was adding a bookmark, false when it was removing one. */
  added: boolean;
}

/**
 * The i18n key naming what went wrong, or null when nothing did.
 *
 * Lives here, pure and exported, rather than as a closure in the view: the
 * mapping has four outcomes that must stay told apart, and pinning all four
 * through a mounted app costs a real parse and a lazily transformed report
 * chunk per case, which is how a correct mapping gets reported as a broken one
 * on a loaded machine. The view keeps only the wiring, which is what a view
 * test should be proving anyway.
 *
 * "Full" is said only when the storage said full. A blocked or unreadable
 * storage is a different fact, and a browser that stores nothing for this site
 * is a third: telling the reader to remove a bookmark and try again is useless
 * advice in the last two.
 */
export function bookmarkFailureKey(written: BookmarkWrite): string | null {
  if (written.ok) return null;
  if (!written.added) return "bm.removeFailed";
  if (written.kind === "quota-exceeded") return "bm.saveFailedFull";
  return written.kind ? "bm.saveFailedRefused" : "bm.saveFailedNoStore";
}

function bookmarkWrite(
  previous: LibEntry[],
  next: LibEntry[],
  added: boolean,
): BookmarkWrite {
  const result = write(local(), BOOKS_KEY, next, { report: false });
  if (result.ok) return { list: next, ok: true, added };
  return { list: previous, ok: false, kind: result.error?.kind, added };
}

export function getBookmarks(): LibEntry[] {
  return read(local(), BOOKS_KEY, LEGACY_BOOKS_KEY);
}
export function toggleBookmark(e: LibEntry): BookmarkWrite {
  const list = getBookmarks();
  const exists = list.some((x) => x.id === e.id);
  const next = exists
    ? list.filter((x) => x.id !== e.id)
    : [e, ...list].slice(0, BOOKS_CAP);
  return bookmarkWrite(list, next, !exists);
}
export function removeBookmark(id: string): BookmarkWrite {
  const list = getBookmarks();
  return bookmarkWrite(
    list,
    list.filter((x) => x.id !== id),
    false,
  );
}
